import type { Decoracion } from '../../api/ops/sala';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Decoración del plano (8.6 y 8.7, G882; encargo de diseño del 01-nov §1; esquema `Decoration` de `sala.yaml`), sin
 * pantalla, para probarla sola. Lo que se ve y no se reserva: paredes, barra, escenario, puertas, plantas, textos y otras
 * piezas. La pared es la única por puntos (`geometry.points`, como mucho 200); las demás son rectángulos con `rotation` de
 * 0 a 359. Cerrar una pared repite el primer punto al final (el contrato no tiene un campo «cerrada») y ese punto de cierre
 * cuenta dentro de los 200 (lo exige el back, C.71). `Decoration` no lleva identidad: en el panel una pieza es su posición
 * en `decorations`.
 */
export type TipoPieza = Decoracion['kind'];
export type Punto = [number, number];

export const MAX_PIEZAS = 2000;
export const MAX_PUNTOS = 200;
export const MAX_TEXTO = 40;
export const GROSOR_PARED = 14;
/** Paso de la rejilla al mover y de los grados al girar con «Ajustar a la rejilla». */
export const PASO_REJILLA = 10;
export const PASO_GIRO = 15;

/** Los siete tipos con su icono y su tamaño inicial (el del dibujo). */
export const TIPOS: readonly { tipo: TipoPieza; nombre: string; icono: NombreIcono; ancho: number; alto: number }[] = [
    { tipo: 'wall', nombre: 'Pared', icono: 'minus', ancho: 200, alto: 14 },
    { tipo: 'bar', nombre: 'Barra', icono: 'utensils', ancho: 200, alto: 90 },
    { tipo: 'stage', nombre: 'Escenario', icono: 'star', ancho: 220, alto: 140 },
    { tipo: 'door', nombre: 'Puerta', icono: 'door-open', ancho: 120, alto: 20 },
    { tipo: 'plant', nombre: 'Planta', icono: 'sprout', ancho: 44, alto: 44 },
    { tipo: 'text', nombre: 'Texto', icono: 'type', ancho: 160, alto: 40 },
    { tipo: 'other', nombre: 'Otro', icono: 'shapes', ancho: 100, alto: 100 },
];

export const NOMBRE_TIPO = Object.fromEntries(TIPOS.map((t) => [t.tipo, t.nombre])) as Readonly<Record<TipoPieza, string>>;

/** Ejemplo del «Nombre (opcional)» de cada tipo (el del dibujo). */
export const EJEMPLO_NOMBRE: Readonly<Record<TipoPieza, string>> = {
    wall: 'Muro del patio', bar: 'Barra principal', stage: 'Tablao', door: 'Entrada', plant: 'Ventanal', text: 'Terraza', other: 'Ventanal',
};

/** Nombre de una pieza para leerla en voz alta: «Barra», «Barra «Barra principal»», «Texto «Terraza»». */
export function nombreDePieza(d: Decoracion): string {
    const nombre = (d.label ?? '').trim();
    return d.kind === 'text' ? `Texto «${nombre}»` : nombre === '' ? NOMBRE_TIPO[d.kind] : `${NOMBRE_TIPO[d.kind]} «${nombre}»`;
}

/** Clave de una pieza en la selección del editor (los recursos van por su `resource_id`). */
export const claveDeco = (indice: number) => `deco:${String(indice)}`;
export const indiceDeClave = (clave: string): number | null => (clave.startsWith('deco:') ? Number(clave.slice(5)) : null);

export const ajustar = (v: number, paso: number) => Math.round(v / paso) * paso;

/** Los puntos de una pared (`null` si la pieza es un rectángulo). */
export function puntosDe(d: Decoracion): Punto[] | null {
    const p = d.geometry.points;
    return p === undefined ? null : p.map((q) => [q[0] ?? 0, q[1] ?? 0]);
}

/** Cerrada: el último punto repite el primero (cuatro puntos o más: tres distintos y el de cierre). */
export function estaCerrada(puntos: readonly Punto[]): boolean {
    const a = puntos[0];
    const z = puntos[puntos.length - 1];
    return puntos.length >= 4 && a !== undefined && z !== undefined && a[0] === z[0] && a[1] === z[1];
}

/** Los puntos sin el de cierre. */
export function puntosAbiertos(puntos: readonly Punto[]): Punto[] {
    return (estaCerrada(puntos) ? puntos.slice(0, -1) : puntos).map((p) => [p[0], p[1]]);
}

/** Los puntos que se guardan: cerrada, con el primero repetido al final. */
export function conCierre(abiertos: readonly Punto[], cerrar: boolean): Punto[] {
    const lista = abiertos.map((p): Punto => [p[0], p[1]]);
    const primero = lista[0];
    return cerrar && lista.length >= 3 && primero !== undefined ? [...lista, [primero[0], primero[1]]] : lista;
}

/** ¿Se puede añadir un punto más? El de cierre cuenta dentro de los 200. */
export function cabeOtroPunto(abiertos: number, cerrada: boolean): boolean {
    return abiertos + 1 + (cerrada ? 1 : 0) <= MAX_PUNTOS;
}

/** ¿Se puede cerrar? Desde tres puntos y con sitio para el de cierre (C.71: con 200 no se cierra). */
export function sePuedeCerrar(abiertos: number): boolean {
    return abiertos >= 3 && abiertos + 1 <= MAX_PUNTOS;
}

// Provisional: el dibujo solo da el texto del tope al añadir puntos; este, para cerrar con 200, lo propone la aplicación.
export const AVISO_CIERRE_LLENO = `Una pared cerrada lleva como mucho ${String(MAX_PUNTOS - 1)} puntos más el de cierre. Quita uno para cerrarla.`;
export const AVISO_PUNTOS = `Una pared lleva como mucho ${String(MAX_PUNTOS)} puntos. Termínala y sigue con otra.`;
export const AVISO_LIMITE = `Este plano tiene ${miles(MAX_PIEZAS)} piezas de decoración, el máximo. Quita alguna para añadir otra.`;

export function miles(n: number): string {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Una pared nueva con sus puntos. */
export function pared(sala: string, abiertos: readonly Punto[], cerrar: boolean): Decoracion {
    return { room_ref: sala, kind: 'wall', geometry: { points: conCierre(abiertos, cerrar) } };
}

/** Una pieza nueva (no pared) centrada en `centro`, con el nombre inicial del dibujo («Texto» y «Barra»). */
export function piezaNueva(tipo: Exclude<TipoPieza, 'wall'>, sala: string, centro: { x: number; y: number }): Decoracion {
    const t = TIPOS.find((x) => x.tipo === tipo) ?? { ancho: 100, alto: 100 };
    const pieza: Decoracion = {
        room_ref: sala,
        kind: tipo,
        geometry: { x: Math.max(0, ajustar(centro.x - t.ancho / 2, PASO_REJILLA)), y: Math.max(0, ajustar(centro.y - t.alto / 2, PASO_REJILLA)), width: t.ancho, height: t.alto, rotation: 0 },
    };
    return tipo === 'text' ? { ...pieza, label: 'Texto' } : tipo === 'bar' ? { ...pieza, label: 'Barra' } : pieza;
}

export interface Caja { x: number; y: number; width: number; height: number }

/** La caja que ocupa la pieza en el plano (la pared, sus puntos con su grosor; el rectángulo, girado). */
export function cajaDePieza(d: Decoracion): Caja {
    const p = puntosDe(d);
    if (p !== null && p.length > 0) {
        const xs = p.map((q) => q[0]);
        const ys = p.map((q) => q[1]);
        const x0 = Math.min(...xs);
        const y0 = Math.min(...ys);
        return { x: x0 - GROSOR_PARED / 2, y: y0 - GROSOR_PARED / 2, width: Math.max(...xs) - x0 + GROSOR_PARED, height: Math.max(...ys) - y0 + GROSOR_PARED };
    }
    const g = d.geometry;
    const x = g.x ?? 0;
    const y = g.y ?? 0;
    const w = g.width ?? 0;
    const h = g.height ?? 0;
    const r = (((g.rotation ?? 0) % 180) + 180) % 180;
    if (r === 0) {
        return { x, y, width: w, height: h };
    }
    const a = (r * Math.PI) / 180;
    const cw = Math.abs(w * Math.cos(a)) + Math.abs(h * Math.sin(a));
    const ch = Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a));
    return { x: x + w / 2 - cw / 2, y: y + h / 2 - ch / 2, width: cw, height: ch };
}

/** Mover una pieza: la pared mueve todos sus puntos. */
export function moverPieza(d: Decoracion, dx: number, dy: number): Decoracion {
    const p = d.geometry.points;
    if (p !== undefined) {
        return { ...d, geometry: { ...d.geometry, points: p.map((q) => [(q[0] ?? 0) + dx, (q[1] ?? 0) + dy]) } };
    }
    return { ...d, geometry: { ...d.geometry, x: (d.geometry.x ?? 0) + dx, y: (d.geometry.y ?? 0) + dy } };
}

/** Duplicar: la copia sale 40 más abajo y a la derecha (como en el dibujo). */
export function duplicarPieza(d: Decoracion): Decoracion {
    return moverPieza(structuredClone(d), 40, 40);
}

/** Cambiar el tipo: de rectángulo a pared se traza su eje; de pared a rectángulo, su caja. */
export function cambiarTipo(d: Decoracion, tipo: TipoPieza): Decoracion {
    if (d.kind === tipo) {
        return d;
    }
    const puntos = puntosDe(d);
    const { label, ...resto } = d;
    const conNombre = (x: Decoracion): Decoracion => (tipo === 'text' && (label ?? '').trim() === '' ? { ...x, label: 'Texto' } : label === undefined ? x : { ...x, label });
    if (tipo === 'wall' && puntos === null) {
        const g = d.geometry;
        const x = g.x ?? 0;
        const y = g.y ?? 0;
        const w = g.width ?? 0;
        const h = g.height ?? 0;
        const eje: Punto[] = w >= h ? [[x, Math.round(y + h / 2)], [x + w, Math.round(y + h / 2)]] : [[Math.round(x + w / 2), y], [Math.round(x + w / 2), y + h]];
        return conNombre({ ...resto, kind: 'wall', geometry: { points: eje } });
    }
    if (tipo !== 'wall' && puntos !== null) {
        const c = cajaDePieza(d);
        return conNombre({ ...resto, kind: tipo, geometry: { x: Math.round(c.x), y: Math.round(c.y), width: Math.max(20, Math.round(c.width)), height: Math.max(20, Math.round(c.height)), rotation: 0 } });
    }
    return conNombre({ ...resto, kind: tipo });
}

/** Grados entre 0 y 359. */
export function grados(v: number): number {
    return ((Math.round(v) % 360) + 360) % 360;
}

/** El giro que marca el asa (encima de la pieza = 0°), de 15 en 15 con «Ajustar a la rejilla» y de 1 en 1 sin él. */
export function giroDelAsa(centro: { x: number; y: number }, puntero: { x: number; y: number }, conRejilla: boolean): number {
    const g = (Math.atan2(puntero.x - centro.x, centro.y - puntero.y) * 180) / Math.PI;
    return grados(conRejilla ? ajustar(g, PASO_GIRO) : g);
}

/** Lo más pequeño que deja el asa de tamaño (el dibujo de 8.6: dos veces 10). */
export const TAMANO_MIN = 20;

/**
 * El tamaño que marca el asa de la esquina (8.6, editor): el de antes más lo arrastrado, de 10 en 10 con «Ajustar a la
 * rejilla», entre 20 y la sala (y nunca más de 10.000, el máximo del contrato). Una mesa redonda sigue redonda.
 */
export function tamanoDelAsa(
    antes: { ancho: number; alto: number },
    dx: number,
    dy: number,
    sala: { ancho: number; alto: number },
    conRejilla: boolean,
    redonda = false,
): { ancho: number; alto: number } {
    const a = (v: number) => (conRejilla ? ajustar(v, PASO_REJILLA) : Math.round(v));
    const entre = (v: number, max: number) => Math.max(TAMANO_MIN, Math.min(10000, Math.max(TAMANO_MIN, max), a(v)));
    const ancho = entre(antes.ancho + dx, sala.ancho);
    const alto = entre(antes.alto + dy, sala.alto);
    if (redonda) {
        const m = Math.max(ancho, alto);
        return { ancho: m, alto: m };
    }
    return { ancho, alto };
}

/** El error del nombre o del texto (`null` si cabe): como mucho 40 y, en un texto, que no esté vacío. */
export function errorDeNombre(valor: string, esTexto: boolean): string | null {
    if (valor.length > MAX_TEXTO) {
        return `Como mucho ${String(MAX_TEXTO)} caracteres: sobran ${String(valor.length - MAX_TEXTO)}.`;
    }
    return esTexto && valor.trim() === '' ? 'Escribe el texto o quita la pieza.' : null;
}

/** Ponerle nombre: vacío, sin `label` (el contrato lo deja opcional). */
export function conNombre(d: Decoracion, valor: string): Decoracion {
    const resto: Decoracion = { ...d };
    delete resto.label;
    return valor === '' ? resto : { ...resto, label: valor };
}

/** Tamaño de la letra (en la rejilla) para que el texto quepa en su rectángulo (el del dibujo). */
export function letraDe(d: Decoracion, largo: number): number {
    const w = d.geometry.width ?? 0;
    const h = d.geometry.height ?? 0;
    const n = Math.max(1, largo);
    if (d.kind === 'text') {
        return Math.max(10, Math.min(h * 0.62, (w * 0.92) / (n * 0.68), 160));
    }
    const tope = d.kind === 'door' ? 18 : 22;
    return Math.max(10, Math.min(tope, d.kind === 'door' ? 18 : Math.max(12, h * 0.5), (w * 0.9) / (n * 0.6)));
}

export interface CambiosDecoracion { nuevas: number; cambiadas: number; quitadas: number }

const forma = (d: Decoracion): string => {
    const p = d.geometry.points;
    const g = d.geometry;
    return JSON.stringify(p === undefined ? [g.x ?? 0, g.y ?? 0, g.width ?? 0, g.height ?? 0, g.rotation ?? 0] : p);
};

/**
 * Cambios de decoración entre la versión activa y el borrador, para «Revisar y publicar» (`FloorPlanImpact` no la trae).
 */
// Provisional hasta la pregunta 678: dos piezas son la misma si coinciden sala, tipo y geometría (una pieza movida cuenta como
// quitada y nueva); si solo cambia su nombre, cuenta como «cambiada».
export function cambiosDeDecoracion(activa: readonly Decoracion[], borrador: readonly Decoracion[]): CambiosDecoracion {
    const firma = (d: Decoracion) => JSON.stringify([d.room_ref, d.kind, forma(d)]);
    const libres = activa.map((d) => ({ firma: firma(d), nombre: d.label ?? '', usada: false }));
    const pendientes: { firma: string; nombre: string }[] = [];
    for (const d of borrador) {
        const f = firma(d);
        const igual = libres.find((a) => !a.usada && a.firma === f && a.nombre === (d.label ?? ''));
        if (igual === undefined) {
            pendientes.push({ firma: f, nombre: d.label ?? '' });
        } else {
            igual.usada = true;
        }
    }
    let cambiadas = 0;
    let nuevas = 0;
    for (const d of pendientes) {
        const misma = libres.find((a) => !a.usada && a.firma === d.firma);
        if (misma === undefined) {
            nuevas += 1;
        } else {
            misma.usada = true;
            cambiadas += 1;
        }
    }
    return { nuevas, cambiadas, quitadas: libres.filter((a) => !a.usada).length };
}

/** Las líneas del detalle («3 piezas de decoración nuevas», «1 pieza de decoración cambiada»…). */
export function lineasDeCambios(c: CambiosDecoracion): string[] {
    const linea = (n: number, una: string, varias: string) => `${String(n)} ${n === 1 ? 'pieza' : 'piezas'} de decoración ${n === 1 ? una : varias}`;
    return [
        ...(c.nuevas > 0 ? [linea(c.nuevas, 'nueva', 'nuevas')] : []),
        ...(c.cambiadas > 0 ? [linea(c.cambiadas, 'cambiada', 'cambiadas')] : []),
        ...(c.quitadas > 0 ? [linea(c.quitadas, 'quitada', 'quitadas')] : []),
    ];
}

export const SOLO_DECORACION = 'Solo cambia el dibujo: la decoración no se reserva ni toca ninguna reserva.';
